import type { Location } from '@/types/models/Location.model'
import type { PageType, SearchState } from '@/types/models'
import { Page } from '@/types/models'
import React, { useState } from 'react'
import MiniCarouselInsuranceFilter from '../MiniCarousel/MiniCarouselInsuranceFilter'
import MiniCarouselInsuranceRedirect from '../MiniCarousel/MiniCarouselInsuranceRedirect'
import { isNonEmptyString } from '@/utils/checks'
import {
  isFacetPageType,
  isInsurancePage,
  isLocationPageType
} from '@/domain/useCases/pageTypeCheck'
import Container from '@/components/Container'
import Section from '@/components/Section'
import { InsuranceCarouselHeader } from './InsuranceCarouselHeader'
import { InsuranceCarouselLink } from './InsuranceCarouselLink'
import { shouldShowInsuranceCarouselLink } from '@/domain/feed/useCases/shouldShowInsuranceCarouselLink'
import { PrimaryFocus } from '@/domain/primaryFocus/constant'
import { generateInsuranceCarouselSubTitle } from '@/domain/useCases/generateInsuraceCarouselSubTitle'
type TermData = { type: Page; title: string }

type PageData = {
  location: Location
  term: { title?: string }
  centers_Location: { accepted_insurance: string }
  terms: { edges: TermData[] }
}

export const InsuranceFeedCarousel = ({
  shouldRenderInsuranceSection,
  pageType,
  pageData,
  searchState,
  termData,
  feedPosition,
  titleOverride,
  primaryFocus,
  ...props
}: {
  shouldRenderInsuranceSection: boolean
  pageType: PageType
  pageData: PageData
  searchState?: SearchState
  termData: TermData
  feedPosition: number
  primaryFocus?: PrimaryFocus
  titleOverride: string
}): JSX.Element | null => {
  const insurancesToIgnore = [pageData?.term?.title]

  const id = 'insurance'
  const carouselTitle =
    isNonEmptyString(primaryFocus) &&
    primaryFocus === PrimaryFocus.SUBSTANCE_USE
      ? 'Rehabs by Accepted Insurance'
      : 'Centers by Accepted Insurance'
  const isLocationOrFacetPageType =
    isLocationPageType(pageType) || isFacetPageType(pageType)
  const isConditionPageType = !isLocationOrFacetPageType

  const locationSubTitle = isFacetPageType(pageType)
    ? pageData?.location?.title
    : titleOverride

  const subTitle = generateInsuranceCarouselSubTitle({
    location: locationSubTitle,
    term: termData?.title,
    primaryFocus
  })

  const handleBadLocation = () =>
    !titleOverride ? window?.location?.pathname.split('/')[3] : titleOverride

  const locationSlug = pageData?.location?.slug
  const acceptedInsuranceLink = `/${locationSlug}/insurance-accepted/`

  const [hideCarouselComponent, setHideCarouselComponent] = useState(false)
  const hideEmptyCarouselComponent = (hide: boolean) => {
    setHideCarouselComponent(hide)
  }
  const isNotLocationOrInsurancePage =
    isNonEmptyString(pageType) &&
    !isLocationPageType(pageType) &&
    !isInsurancePage(pageType)

  const shouldDisplayLink = false

  if (!shouldRenderInsuranceSection || termData?.type === Page.INSURANCE) {
    return null
  }

  return (
    <Section
      key={`${feedPosition}`}
      className={`my-10 md:my-12 ${hideCarouselComponent ? 'hidden' : ''}`}
    >
      <Container noPadding={true}>
        <InsuranceCarouselHeader
          title={carouselTitle}
          subTitle={subTitle}
        />

        {isLocationPageType(pageType) && (
          <MiniCarouselInsuranceRedirect
            location={titleOverride}
            id={id}
            itemsToIgnore={insurancesToIgnore}
            terms={pageData?.terms?.edges}
            feedPosition={feedPosition}
            locationAcceptedInsurances={
              pageData?.centers_Location?.accepted_insurance
            }
            locationSlug={locationSlug}
            pageType={pageType}
            hideEmptyCarouselComponent={hideEmptyCarouselComponent}
          />
        )}

        {isNotLocationOrInsurancePage && (
          <MiniCarouselInsuranceFilter
            location={titleOverride || handleBadLocation()}
            id={id}
            searchState={searchState}
            feedPosition={feedPosition}
            itemsToIgnore={insurancesToIgnore}
            terms={pageData?.terms?.edges}
            locationAcceptedInsurances={
              pageData?.centers_Location?.accepted_insurance
            }
            locationSlug={locationSlug}
            pageType={pageType}
            isUnitedStatesCount={isConditionPageType}
            hideEmptyCarouselComponent={hideEmptyCarouselComponent}
          />
        )}

        {shouldDisplayLink && (
          <InsuranceCarouselLink anchorLink={acceptedInsuranceLink} />
        )}
      </Container>
    </Section>
  )
}
